import { useCallback, useEffect, useState } from "react";
import {
  fetchGoldenSummary,
  fetchNextGoldenCase,
  saveGoldenLabels,
  type GoldenCase,
  type GoldenProgress,
} from "@/services/adminModelCurrency";
import { describeApiError } from "./helpers";

/** Drives the labeling workflow: the current case, progress, and save/skip. */
export function useGoldenLabeling() {
  const [goldenCase, setGoldenCase] = useState<GoldenCase | null>(null);
  const [progress, setProgress] = useState<GoldenProgress | null>(null);
  const [writable, setWritable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadNext = useCallback(async (afterId?: string) => {
    try {
      const next = await fetchNextGoldenCase(afterId);
      setGoldenCase(next.case);
      setProgress(next.progress);
      setError(null);
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const summary = await fetchGoldenSummary();
        setWritable(summary.writable);
      } catch (err) {
        setError(describeApiError(err));
      }
      await loadNext();
    })();
  }, [loadNext]);

  async function save(scores: Record<string, number>, notes: string) {
    if (!goldenCase) return;
    setSaving(true);
    try {
      const saved = await saveGoldenLabels(goldenCase.id, scores, notes);
      setProgress(saved.progress);
      await loadNext();
    } catch (err) {
      setError(describeApiError(err));
    } finally {
      setSaving(false);
    }
  }

  async function skip() {
    if (goldenCase) await loadNext(goldenCase.id);
  }

  return { goldenCase, progress, writable, loading, saving, error, save, skip };
}
